import React, { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectUnitToken } from '@homeplay/app';
import { apiGetHotColdNumbers } from '@homeplay/api';
import { useRandomBallGeneration } from '../../hooks/useRandomBallGeneration';
import coldIcon from '../../../../../../shared/assets/images/lotto-assistant/cold.svg';
import hotIcon from '../../../../../../shared/assets/images/lotto-assistant/hot.svg';
import quickPickIcon from '../../../../../../shared/assets/images/lotto-assistant/quick-pick.svg';
import classes from './QuickPickButtons.module.scss';

const pickRandom = (arr, count) => {
  const shuffled = [...arr].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, count);
};

const QuickPickButtons = ({
  ticket,
  editedNumbers,
  setEditedNumbers,
  setValidationError,
  minNormalCount,
  requiredBonusCount,
  lotteryId,
}) => {
  const unitToken = useSelector(selectUnitToken);
  const [hotColdData, setHotColdData] = useState(null);

  const { handleRandomFill } = useRandomBallGeneration({
    ticket,
    editedNumbers,
    setEditedNumbers,
    setValidationError,
    minNormalCount,
    requiredBonusCount,
  });

  const applyHotColdFill = useCallback(
    (type, data) => {
      if (!data?.data) return;

      const normalPool = type === 'hot' ? data.data.hot_numbers_normal : data.data.cold_numbers_normal;
      const bonusPool = type === 'hot' ? data.data.hot_numbers_bonus : data.data.cold_numbers_bonus;

      if (!normalPool || normalPool.length === 0) return;

      const currentNormalCount = editedNumbers.filter((n) => n.type === 'NORMAL').length;
      const currentBonusCount = editedNumbers.filter((n) => n.type === 'BONUS').length;
      const normalCount = currentNormalCount > 0 ? currentNormalCount : minNormalCount;
      const bonusCount = currentBonusCount > 0 ? currentBonusCount : requiredBonusCount;

      const pickedNormal = pickRandom(normalPool, normalCount);
      const normalNumbers = pickedNormal.map((value) => ({ type: 'NORMAL', value }));

      let bonusNumbers = [];
      if (bonusCount > 0) {
        if (!bonusPool || bonusPool.length === 0) return;
        const filteredBonusPool = ticket.secondary_unique
          ? bonusPool.filter((v) => !pickedNormal.includes(v))
          : bonusPool;
        const pickedBonus = pickRandom(filteredBonusPool, bonusCount);
        bonusNumbers = pickedBonus.map((value) => ({ type: 'BONUS', value }));
      }

      setEditedNumbers([...normalNumbers, ...bonusNumbers]);
      setValidationError(null);
    },
    [editedNumbers, minNormalCount, requiredBonusCount, ticket.secondary_unique, setEditedNumbers, setValidationError]
  );

  const handleHotColdFill = useCallback(
    async (type) => {
      if (!lotteryId || !unitToken) return;
      if (hotColdData) {
        applyHotColdFill(type, hotColdData);
        return;
      }
      try {
        const data = await apiGetHotColdNumbers({ unitToken, lotteryId });
        setHotColdData(data);
        applyHotColdFill(type, data);
      } catch (error) {
        console.error('hot/cold fill failed', error);
      }
    },
    [lotteryId, unitToken, hotColdData, applyHotColdFill]
  );

  return (
    <div className={classes.Container}>
      <button type="button" onClick={() => handleHotColdFill('cold')} className={classes.Button + ' ' + classes.Cold}>
        <img src={coldIcon} alt="" width={14} height={15} />
        Cold
      </button>
      <button type="button" onClick={() => handleHotColdFill('hot')} className={classes.Button + ' ' + classes.Hot}>
        <img src={hotIcon} alt="" width={11} height={14} />
        Hot
      </button>
      <button type="button" onClick={handleRandomFill} className={classes.Button + ' ' + classes.QuickPick}>
        <img src={quickPickIcon} alt="" width={9} height={17} />
        Quick pick
      </button>
    </div>
  );
};

export default QuickPickButtons;
